import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import type { ProfileRow, User, UserPreferences } from "../types";
import { profileToUser } from "../types";
import { uuid } from "../utils/uuid";

function splitProfilesByRole(users: User[]) {
  return {
    partners: users.filter((u) => u.role === "partner"),
    installers: users.filter((u) => u.role === "installer"),
    commercials: users.filter((u) => u.role === "commercial"),
  };
}

function mergeUserByRole(list: User[], user: User): User[] {
  const idx = list.findIndex((u) => u.id === user.id);
  if (idx >= 0) {
    const next = [...list];
    next[idx] = user;
    return next;
  }
  return [...list, user];
}

export function useProfiles(ownerId?: string) {
  const [partners, setPartners] = useState<User[]>([]);
  const [installers, setInstallers] = useState<User[]>([]);
  const [commercials, setCommercials] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const upsertProfile = useCallback((row: ProfileRow) => {
    const user = profileToUser(row);
    if (user.role === "partner") setPartners((prev) => mergeUserByRole(prev, user));
    else if (user.role === "installer") setInstallers((prev) => mergeUserByRole(prev, user));
    else if (user.role === "commercial") setCommercials((prev) => mergeUserByRole(prev, user));
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // MULTI-TENANT: Ne pas charger tous les profils si ownerId pas encore connu (évite afficher partenaires/poseurs des autres clients au refresh)
      if (!ownerId) {
        setPartners([]);
        setInstallers([]);
        setCommercials([]);
        setLoading(false);
        return;
      }
      const { data, error: e } = await supabase
        .from("profiles")
        .select("*")
        .eq("active", true)
        .eq("owner_id", ownerId);
      if (e) throw e;
      const rows = (data ?? []) as ProfileRow[];
      const users = rows.map(profileToUser);
      const split = splitProfilesByRole(users);
      setPartners(split.partners);
      setInstallers(split.installers);
      setCommercials(split.commercials);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur chargement profils");
      // Ne pas vider les listes : évite que les commerciaux « disparaissent » après un refetch raté.
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const addProfile = useCallback(
    async (p: {
      name: string;
      code: string;
      phone?: string;
      email?: string;
      role: "partner" | "installer" | "commercial";
      defaultStockRegionId?: string;
    }) => {
      try {
        const id = uuid();
        
        // MULTI-TENANT: Ajouter préfixe automatique au code (ex: FE27-123)
        const prefix = ownerId ? ownerId.substring(0, 4).toUpperCase() : "";
        const finalCode = prefix ? `${prefix}-${p.code.toUpperCase()}` : p.code.toUpperCase();
        
        const preferences = p.role === "installer" && p.defaultStockRegionId
          ? { defaultStockRegionId: p.defaultStockRegionId }
          : null;
        const row = {
          id,
          name: p.name,
          code: finalCode,
          phone: p.phone || null,
          email: p.email || null,
          role: p.role,
          active: true,
          owner_id: ownerId,
          ...(preferences && { preferences }),
        };
        const { data, error: e } = await supabase.from("profiles").insert(row).select().single();
        if (e) throw e;
        const user = profileToUser(data as import("../types").ProfileRow);
        if (p.role === "partner") setPartners((prev) => [...prev, user]);
        else if (p.role === "installer") setInstallers((prev) => [...prev, user]);
        else setCommercials((prev) => [...prev, user]);
        return { data: user, error: null };
      } catch (err) {
        return {
          data: null,
          error: err instanceof Error ? err.message : "Erreur ajout profil",
        };
      }
    },
    [ownerId]
  );

  const updateProfile = useCallback(
    async (id: string, updates: { name?: string; email?: string; preferences?: UserPreferences }) => {
      try {
        const updateData: Record<string, unknown> = {
          updated_at: new Date().toISOString(),
        };
        if (updates.name !== undefined) updateData.name = updates.name;
        if (updates.email !== undefined) updateData.email = updates.email || null;
        if (updates.preferences !== undefined) updateData.preferences = updates.preferences;

        const { data, error: e } = await supabase
          .from("profiles")
          .update(updateData)
          .eq("id", id)
          .select()
          .single();
        
        if (e) throw e;
        
        const updatedUser = profileToUser(data as import("../types").ProfileRow);
        
        // Mettre à jour l'état local
        if (updatedUser.role === "partner") {
          setPartners((prev) => prev.map((u) => (u.id === id ? updatedUser : u)));
        } else if (updatedUser.role === "installer") {
          setInstallers((prev) => prev.map((u) => (u.id === id ? updatedUser : u)));
        } else if (updatedUser.role === "commercial") {
          setCommercials((prev) => prev.map((u) => (u.id === id ? updatedUser : u)));
        }
        
        return { data: updatedUser, error: null };
      } catch (err) {
        return {
          data: null,
          error: err instanceof Error ? err.message : "Erreur modification profil",
        };
      }
    },
    []
  );

  const removeProfile = useCallback(
    async (id: string, role: "partner" | "installer" | "commercial") => {
      if (!ownerId) return { error: "Non autorisé" };
      try {
        // D’abord rendre les dossiers "non assignés" (sans perdre les dossiers)
        if (role === "partner") {
          const { error: jobsErr } = await supabase
            .from("jobs")
            .update({ partner_id: null })
            .eq("owner_id", ownerId)
            .eq("partner_id", id);
          if (jobsErr) throw jobsErr;
          const { error: invErr } = await supabase
            .from("inventory")
            .update({ partner_id: null })
            .eq("owner_id", ownerId)
            .eq("partner_id", id);
          if (invErr) throw invErr;
        } else if (role === "installer") {
          const { error: jobsErr } = await supabase
            .from("jobs")
            .update({ installer_id: null })
            .eq("owner_id", ownerId)
            .eq("installer_id", id);
          if (jobsErr) throw jobsErr;
        } else {
          const { error: jobsErr } = await supabase
            .from("jobs")
            .update({ commercial_id: null })
            .eq("owner_id", ownerId)
            .eq("commercial_id", id);
          if (jobsErr) throw jobsErr;
        }

        const { error: e } = await supabase
          .from("profiles")
          .delete()
          .eq("id", id)
          .eq("owner_id", ownerId);
        if (e) throw e;
        if (role === "partner") setPartners((prev) => prev.filter((u) => u.id !== id));
        else if (role === "installer") setInstallers((prev) => prev.filter((u) => u.id !== id));
        else setCommercials((prev) => prev.filter((u) => u.id !== id));
        return { error: null };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isConflict =
          msg.includes("409") ||
          msg.toLowerCase().includes("conflict") ||
          (err && typeof err === "object" && "code" in err && String((err as { code: string }).code) === "23503");
        const friendlyError = isConflict
          ? "Ce partenaire ou poseur a encore des dossiers (ou d’autres données) liés. Réattribuez ou supprimez-les d’abord."
          : msg;
        return { error: friendlyError };
      }
    },
    [ownerId]
  );

  return {
    partners,
    installers,
    commercials,
    loading,
    error,
    refetch: fetchAll,
    upsertProfile,
    addProfile,
    updateProfile,
    removeProfile,
  };
}
